/**
 * 宿主适配契约（Host Adapter）
 *
 * 工作流编辑器包对宿主应用的全部依赖都收敛在这里：HTTP 客户端、SSE、鉴权/项目上下文、
 * 日志、模型目录、MCP 工具、文件上传、知识库、Monaco 加载等。
 *
 * 约束：
 * - 包内代码禁止 import 宿主路径（`@/...`），只能通过 `getWorkflowEditorHost()` 取能力。
 * - 宿主在应用启动时调用 `setWorkflowEditorHost()` 装配；测试可局部覆盖。
 * - 这里的数据类型只声明包内实际用到的字段，宿主的完整 DTO 结构性兼容即可。
 */
import type { Ref } from 'vue'
import type * as Monaco from 'monaco-editor'

// ───────────────────────── HTTP ─────────────────────────

export interface WorkflowHttpRequestConfig {
  /** 查询参数；用 object 而非 Record 以便直接传接口类型的 DTO */
  params?: object
  headers?: Record<string, string>
  responseType?: 'json' | 'blob' | 'text' | 'arraybuffer'
  timeout?: number
  signal?: AbortSignal
}

/** 与宿主 apiClient 对齐：响应拦截器已解包，直接返回 data。 */
export interface WorkflowHttpClient {
  get<T = unknown>(url: string, config?: WorkflowHttpRequestConfig): Promise<T>
  post<T = unknown>(url: string, data?: unknown, config?: WorkflowHttpRequestConfig): Promise<T>
  put<T = unknown>(url: string, data?: unknown, config?: WorkflowHttpRequestConfig): Promise<T>
  delete<T = unknown>(url: string, config?: WorkflowHttpRequestConfig): Promise<T>
  patch<T = unknown>(url: string, data?: unknown, config?: WorkflowHttpRequestConfig): Promise<T>
}

// ───────────────────────── SSE ─────────────────────────

export interface WorkflowSseEvent {
  id: string
  event: string
  data: string
}

export interface WorkflowSseClientOptions {
  url: string
  method?: 'GET' | 'POST'
  headers?: Record<string, string>
  body?: string | Record<string, unknown>
  onMessage: (event: WorkflowSseEvent) => void | boolean
  onOpen?: () => void
  onError?: (error: Error) => void
  onClose?: () => void
  onReconnectExhausted?: (attempts: number) => void
  maxRetries?: number
  initialRetryDelay?: number
  maxRetryDelay?: number
}

export interface WorkflowSseClient {
  connect(): Promise<void> | void
  disconnect(): void
}

// ───────────────────────── 鉴权 / 日志 ─────────────────────────

export interface WorkflowAuthContext {
  hasAccessToken(): boolean
  getAccessToken(): string
  /** 当前项目 ID；未选项目时返回空串。 */
  getProjectId(): string
}

export interface WorkflowLogger {
  debug(...args: unknown[]): void
  info(...args: unknown[]): void
  warn(...args: unknown[]): void
  error(...args: unknown[]): void
}

// ───────────────────────── 模型目录 ─────────────────────────

export type CatalogCurrency = 'CNY' | 'USD'
export type CatalogModelType = 'llm' | 'embedding' | 'rerank' | 'multimodal' | 'image' | 'video'

/** 与宿主「模型管理」CatalogModel 对齐。 */
export interface CatalogModel {
  model_id: string
  provider: string
  display_name: string
  model_type?: CatalogModelType
  embedding_dimension?: number | null
  supported_dimensions?: number[] | null
  supports_tools: boolean
  supports_vision: boolean
  supports_image_gen: boolean
  supports_video_gen: boolean
  cost_per_1k_prompt: number
  cost_per_1k_completion: number
  cost_per_image: number
  currency: CatalogCurrency
  context_window: number
  max_output_tokens: number | null
  downgrade_to: string | null
  tags: string[]
  is_enabled: boolean
  is_deprecated?: boolean
  created_at?: string
  updated_at?: string
}

export interface FetchModelCatalogParams {
  available_only?: boolean
  model_type?: CatalogModelType
  provider?: string
}

/** 模型目录的响应式数据源（宿主通常用进程级缓存实现）。 */
export interface WorkflowModelCatalog {
  models: Readonly<Ref<readonly CatalogModel[]>>
  loading: Readonly<Ref<boolean>>
  error: Readonly<Ref<Error | null>>
  refresh: () => Promise<CatalogModel[]>
  findById: (id: string) => CatalogModel | undefined
  findByProviderAndName: (provider: string, name: string) => CatalogModel | undefined
}

// ───────────────────────── MCP 工具 ─────────────────────────

export interface MCPProviderConfiguration {
  timeout: string
  sse_read_timeout: string
}

export interface MCPProviderTool {
  name: string
  description?: string
  inputSchema?: Record<string, unknown>
}

export interface MCPToolProvider {
  id: string
  name: string
  project_id: string
  user_id: string
  server_identifier: string
  server_url: string
  raw_server_url?: string | null
  icon: string
  is_team_authorization: boolean
  tools: MCPProviderTool[]
  configuration: MCPProviderConfiguration
  masked_headers: Record<string, string>
  headers?: Record<string, string>
  authentication?: Record<string, string>
  created_at: number
  updated_at: number
}

// ───────────────────────── 文件 ─────────────────────────

export interface WorkflowFileUploadParams {
  description?: string
  category?: string
  project_id?: string
  tags?: string[]
}

export interface WorkflowUploadedFile {
  id: string
  name: string
  original_name: string
  content_type: string
  size: number
}

// ───────────────────────── 知识库 ─────────────────────────

export interface WorkflowKnowledgeBaseGroup {
  id: string
  name: string
}

export interface WorkflowKnowledgeBaseRecord {
  id: string
  name: string
  type?: string
  tags?: string[]
  groupId?: string
  settings?: { retrieverType?: string }
}

/** 知识库列表的响应式数据源（宿主用 Pinia store 实现）。 */
export interface WorkflowKnowledgeBaseSource {
  knowledgeBases: Readonly<Ref<readonly WorkflowKnowledgeBaseRecord[]>>
  groups: Readonly<Ref<readonly WorkflowKnowledgeBaseGroup[]>>
  load(params?: { status?: string }): Promise<void>
}

// ───────────────────────── 宿主契约 ─────────────────────────

export interface WorkflowEditorHost {
  http: WorkflowHttpClient
  createSseClient(options: WorkflowSseClientOptions): WorkflowSseClient
  auth: WorkflowAuthContext
  logger: WorkflowLogger
  generateUUID(): string
  /** 展示用时间格式化（宿主决定时区与格式）。 */
  formatDateTime(value: string | number | Date | null | undefined): string
  getErrorMessage(error: unknown, fallback?: string): string
  /** 按需加载 Monaco（Code 节点编辑器用）。 */
  loadMonaco(): Promise<typeof Monaco>
  models: {
    fetchCatalog(params?: FetchModelCatalogParams): Promise<CatalogModel[]>
    useCatalog(autoLoad?: boolean): WorkflowModelCatalog
  }
  mcp: {
    listProviders(): Promise<MCPToolProvider[]>
  }
  files: {
    upload(file: File, params?: WorkflowFileUploadParams): Promise<WorkflowUploadedFile>
    getDownloadUrl(fileId: string): string
  }
  knowledgeBases: {
    /** 只能在组件 setup 内调用（宿主实现可能依赖 Pinia）。 */
    use(): WorkflowKnowledgeBaseSource
  }
}

let currentHost: WorkflowEditorHost | null = null

export function setWorkflowEditorHost(host: WorkflowEditorHost): void {
  currentHost = host
}

/** 仅测试使用：清空装配，验证未装配路径。 */
export function resetWorkflowEditorHost(): void {
  currentHost = null
}

export function getWorkflowEditorHost(): WorkflowEditorHost {
  if (!currentHost) {
    throw new Error(
      '[workflow-editor] 宿主未装配：请在应用启动时调用 setWorkflowEditorHost()（web-app 见 src/plugins/workflow-editor-host.ts）',
    )
  }
  return currentHost
}

// ───────────────────────── 惰性包装器 ─────────────────────────
// 模块顶层可以安全持有这些引用：真正取宿主发生在调用时刻，不要求装配先于 import。

// rest 参数原样转发：保持调用元数，宿主侧断言（toHaveBeenCalledWith）才不会多出 undefined
export const hostHttp: WorkflowHttpClient = {
  get: (...args) => getWorkflowEditorHost().http.get(...args),
  post: (...args) => getWorkflowEditorHost().http.post(...args),
  put: (...args) => getWorkflowEditorHost().http.put(...args),
  delete: (...args) => getWorkflowEditorHost().http.delete(...args),
  patch: (...args) => getWorkflowEditorHost().http.patch(...args),
}

export const hostLogger: WorkflowLogger = {
  debug: (...args) => getWorkflowEditorHost().logger.debug(...args),
  info: (...args) => getWorkflowEditorHost().logger.info(...args),
  warn: (...args) => getWorkflowEditorHost().logger.warn(...args),
  error: (...args) => getWorkflowEditorHost().logger.error(...args),
}

export function generateUUID(): string {
  return getWorkflowEditorHost().generateUUID()
}

export function getProjectId(): string {
  return getWorkflowEditorHost().auth.getProjectId()
}

export function formatDateTime(value: string | number | Date | null | undefined): string {
  return getWorkflowEditorHost().formatDateTime(value)
}

export function getErrorMessage(error: unknown, fallback?: string): string {
  return getWorkflowEditorHost().getErrorMessage(error, fallback)
}

export function loadMonaco(): Promise<typeof Monaco> {
  return getWorkflowEditorHost().loadMonaco()
}
